import { LevelUpProps } from './level-up.interface';
import Lottie from 'lottie-react';
import confettiLottie from 'src/assets/animations/lottie/confetti.json';
import Coin from 'src/assets/images/icons/custom-icons/Coin';
import Button from 'src/components/Button/Button';
import { ButtonSize } from 'src/components/Button/button-size.enum';
import { ButtonState } from 'src/components/Button/button-state.enum';
import { ButtonType } from 'src/components/Button/button-type.enum';
import { RewardProgress } from 'src/components/GamificationPanel/RewardProgress/RewardProgress';
import Modal from 'src/components/Modal/Modal';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import { useTranslationByKey } from 'src/hooks/use-translation-by-key';
import { useUserCurrency } from 'src/hooks/use-user-currency';

// Level up celebration floating elements - reduced and more subtle
const LevelUpFloatingElements: React.FC = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-20">
    <div
      className="absolute top-8 left-[15%] text-xl animate-bounce"
      style={{ animationDelay: '0s', animationDuration: '2.5s' }}
    >
      ⭐
    </div>
    <div
      className="absolute top-12 right-[20%] text-lg animate-bounce"
      style={{ animationDelay: '1s', animationDuration: '3s' }}
    >
      ✨
    </div>
    <div
      className="absolute bottom-24 left-[20%] text-lg animate-bounce"
      style={{ animationDelay: '0.5s', animationDuration: '2.8s' }}
    >
      🏆
    </div>
  </div>
);

/**
 * LevelUp modal component.
 */
const LevelUp: React.FC<LevelUpProps> = ({ isOpen, onClose, levelReached, pointsEarned, rewardProgress }) => {
  const t = useTranslationByKey('COMPONENTS.LEVEL_UP_OVERLAY');
  const { isMobile } = useBreakpoint();
  const { formatPoints } = useUserCurrency();

  const handleClick = () => {
    // TODO: Do reward progress check as specified in the design
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onClose();
      }}
      hideCloseButton={true}
      className={isMobile ? '!w-full box-content' : '!max-w-[480px]'}
      modalBodyClassName="md:w-[480px] !bg-gradient-to-br !from-purple-50 !via-indigo-50 !to-blue-50 !border-2 !border-white/40 !rounded-2xl !shadow-xl overflow-hidden"
    >
      {/* Confetti Lottie Animation - background, only when open */}
      {isOpen && (
        <div className="absolute inset-0 z-50 pointer-events-none overflow-hidden opacity-40">
          <Lottie animationData={confettiLottie} loop={false} autoplay style={{ width: '100%', height: '100%' }} />
        </div>
      )}
      <div className="relative">
        <LevelUpFloatingElements />

        <div className="relative z-10 flex flex-col gap-4 p-5 md:p-6">
          {/* Main Header */}
          <div className="text-center">
            <h1 className="text-xl md:text-2xl font-bold text-gray-800 mb-1">{t('CELEBRATION_HEADER.TITLE')}</h1>
            <p className="text-gray-600 text-sm">{t('CELEBRATION_HEADER.SUBTITLE')}</p>
          </div>

          {/* Level Achievement Card */}
          <div className="bg-white/60 backdrop-blur-sm rounded-xl p-4 border border-white/60 shadow-sm">
            <div className="text-center">
              <h2 className="text-lg font-bold text-gray-800 mb-3">
                {t('LEVEL_REACHED', { levelReached: levelReached })}
              </h2>

              {pointsEarned && (
                <div className="flex flex-col items-center gap-3">
                  <p className="text-gray-700 text-sm">{t('YOU_ARE_AMAZING')}</p>
                  <div className="flex items-center bg-gradient-to-r from-yellow-100 to-orange-100 rounded-full px-4 py-2 border border-yellow-200 shadow-sm">
                    <Coin className="w-5 h-5" />
                    <span className="font-bold text-orange-700 ml-2">{formatPoints(pointsEarned)}</span>
                  </div>
                  <p className="text-gray-600 text-sm">
                    {t('FOR')} {t('LEVELING_UP')}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Reward Progress Section */}
          {rewardProgress && (
            <div>
              <div className="text-center mb-4">
                <h3 className="text-gray-700 font-semibold text-base">{t('REWARD_PROGRESS_SECTION.TITLE')}</h3>
              </div>
              <RewardProgress {...rewardProgress} />
            </div>
          )}

          {/* Action Button */}
          <Button
            label={t('BUTTON_TITLE')}
            onClick={handleClick}
            state={ButtonState.Enabled}
            type={ButtonType.Filled}
            size={ButtonSize.Big}
            className="w-full"
          />
        </div>
      </div>
    </Modal>
  );
};

export default LevelUp;
